import React, { useState, useEffect } from 'react';
import { Play, Settings, TrendingUp, RotateCcw, Brain, AlertCircle, Lock } from 'lucide-react';
import { DIMENSIONS } from '../../constants/gameConstants';
import { useAnonymous } from '../../contexts/AnonymousContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useAuth } from '../../contexts/AuthContext';
import UpgradeModal from '../UpgradeModal';
import { colors, typography, spacing, radius, breakpoints } from '../../../styles/theme';

const RoundSummary = ({ 
  roundSummary, 
  continueTraining, 
  resetGame 
}) => {
  const { roundsToday: anonRoundsToday, dailyLimit: anonDailyLimit } = useAnonymous();
  const { tier, usageInfo, tierRules } = useSubscription();
  const { isAuthenticated } = useAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const mobile = windowWidth < parseInt(breakpoints.mobile);
  
  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: mobile ? '0 0.5rem' : '0'
    }}>
      {/* Round Progress Tracker - For Anonymous and Free Users */}
      {!isAuthenticated && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: `1px solid rgba(239, 68, 68, 0.3)`,
          borderRadius: radius.lg,
          padding: spacing[4],
          marginBottom: spacing[4],
          fontFamily: typography.fontSans
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing[2]
          }}>
            <div style={{
              fontSize: typography.sm,
              fontWeight: typography.semibold,
              color: '#ef4444'
            }}>
              Trial Rounds
            </div>
            <div style={{
              fontSize: typography.lg,
              fontWeight: typography.bold,
              color: '#ef4444'
            }}>
              {anonRoundsToday} of {anonDailyLimit}
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: spacing[1],
            marginBottom: spacing[2]
          }}>
            {[...Array(anonDailyLimit)].map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '8px',
                  borderRadius: radius.sm,
                  background: i < anonRoundsToday 
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'rgba(100, 116, 139, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
          <div style={{
            fontSize: typography.xs,
            color: colors.textTertiary,
            textAlign: 'center'
          }}>
            {anonRoundsToday >= anonDailyLimit 
              ? '🎯 Trial complete! Sign up for 8 rounds/day'
              : `${anonDailyLimit - anonRoundsToday} trial rounds left today`}
          </div>
        </div>
      )}

      {isAuthenticated && tier === 'free' && usageInfo && (
        <div style={{
          background: 'rgba(96, 165, 250, 0.1)',
          border: `1px solid rgba(96, 165, 250, 0.3)`,
          borderRadius: radius.lg,
          padding: spacing[4],
          marginBottom: spacing[4],
          fontFamily: typography.fontSans
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing[2]
          }}>
            <div style={{
              fontSize: typography.sm,
              fontWeight: typography.semibold,
              color: colors.primary
            }}>
              Daily Rounds
            </div>
            <div style={{
              fontSize: typography.lg,
              fontWeight: typography.bold,
              color: colors.primary
            }}>
              {usageInfo.roundsToday} of {tierRules.dailyRounds}
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: spacing[1],
            marginBottom: spacing[2]
          }}>
            {[...Array(tierRules.dailyRounds)].map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '8px',
                  borderRadius: radius.sm,
                  background: i < usageInfo.roundsToday 
                    ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`
                    : 'rgba(100, 116, 139, 0.2)',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
          <div style={{
            fontSize: typography.xs,
            color: colors.textTertiary,
            textAlign: 'center'
          }}>
            {usageInfo.remainingToday > 0 
              ? `${usageInfo.remainingToday} rounds remaining today`
              : '🎯 Daily limit reached! Upgrade for unlimited'}
          </div>
        </div>
      )}

      <div style={{
        background: 'rgba(30, 41, 59, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: mobile ? '1.25rem' : '2rem',
        border: '1px solid rgba(100, 116, 139, 0.3)',
        textAlign: 'center'
      }}>
        {/* Anonymous user notice */}
        {roundSummary.anonymous && !roundSummary.limitError && (
          <div style={{
            background: 'rgba(59, 130, 246, 0.2)',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <h3 style={{
              margin: '0 0 0.25rem 0',
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#60a5fa'
            }}>
              ⚠️ Progress Not Saved
            </h3>
            <p style={{
              margin: '0 0 0.75rem 0',
              fontSize: '0.9rem',
              color: '#e2e8f0',
              lineHeight: '1.4'
            }}>
              Sign up for a free account to track your improvement!
            </p>
            <button
              onClick={() => window.location.href = '/signup'}
              style={{
                padding: '0.6rem 1.25rem',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Sign Up Free - Get 8 Rounds/Day
            </button>
          </div>
        )}

        {/* Save status / Limit error indicator */}
        {roundSummary.limitError && (
          <div style={{
            background: roundSummary.limitError.type === 'daily' 
              ? 'rgba(239, 68, 68, 0.2)' 
              : 'rgba(234, 179, 8, 0.2)',
            border: `2px solid ${roundSummary.limitError.type === 'daily' ? '#ef4444' : '#f59e0b'}`,
            borderRadius: '12px',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              {roundSummary.limitError.type === 'daily' ? (
                <Lock size={32} color="#ef4444" />
              ) : (
                <AlertCircle size={32} color="#f59e0b" />
              )}
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '700',
                color: roundSummary.limitError.type === 'daily' ? '#ef4444' : '#f59e0b'
              }}>
                {roundSummary.limitError.type === 'daily' && 'Daily Limit Reached!'}
                {roundSummary.limitError.type === 'dimensions' && 'Dimensions Not Allowed'}
              </h3>
            </div>
            <p style={{
              margin: '0 0 1rem 0',
              fontSize: '1rem',
              color: '#e2e8f0',
              lineHeight: '1.5'
            }}>
              {roundSummary.limitError.message}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              {roundSummary.limitError.type === 'anonymous' && (
                <button
                  onClick={() => window.location.href = '/signup'}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Sign Up for Free Account
                </button>
              )}
              {roundSummary.limitError.type === 'daily' && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Upgrade for Unlimited Rounds
                </button>
              )}
              {roundSummary.limitError.type === 'daily' && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Upgrade for Unlimited Rounds
                </button>
              )}
            </div>
          </div>
        )}
        {roundSummary.saved === false && !roundSummary.limitError && !roundSummary.anonymous && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.2)',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            padding: '0.75rem',
            marginBottom: '1rem',
            color: '#ef4444',
            fontSize: '0.9rem',
            fontWeight: '600'
          }}>
            ⚠️ Failed to save progress to server. Your round data may not be saved.
          </div>
        )}
        
        {/* Title based on recommendation */}
        <div style={{ marginBottom: '1.5rem' }}>
          {roundSummary.recommendation === 'level_up' && (
            <>
              <TrendingUp size={50} color="#10b981" style={{ marginBottom: '0.75rem' }} />
              <h2 style={{
                margin: '0 0 0.25rem 0',
                fontSize: '2rem',
                fontWeight: '800',
                color: '#10b981'
              }}>
                Level Up!
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
                Excellent performance! Moving to {roundSummary.newLevel}-back
              </p>
            </>
          )}
          {roundSummary.recommendation === 'level_down' && (
            <>
              <RotateCcw size={50} color="#f59e0b" style={{ marginBottom: '0.75rem' }} />
              <h2 style={{
                margin: '0 0 0.25rem 0',
                fontSize: '2rem',
                fontWeight: '800',
                color: '#f59e0b'
              }}>
                Level Adjusted
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
                Adjusting difficulty to {roundSummary.newLevel}-back for optimal training
              </p>
            </>
          )}
          {roundSummary.recommendation === 'same_level' && (
            <>
              <Brain size={50} color="#60a5fa" style={{ marginBottom: '0.75rem' }} />
              <h2 style={{
                margin: '0 0 0.25rem 0',
                fontSize: '2rem',
                fontWeight: '800',
                color: '#60a5fa'
              }}>
                Round Complete
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
                Good training! Continuing at {roundSummary.currentLevel}-back
              </p>
            </>
          )}
        </div>

        {/* Dimension Results */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          borderRadius: '12px',
          padding: mobile ? '1rem' : '1.5rem',
          marginBottom: mobile ? '1rem' : '1.5rem'
        }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            fontSize: mobile ? '1rem' : '1.2rem',
            fontWeight: '700',
            color: '#cbd5e1'
          }}>
            Performance Breakdown
          </h3>
          <p style={{
            margin: '0 0 1rem 0',
            fontSize: mobile ? '0.75rem' : '0.85rem',
            color: '#94a3b8',
            textAlign: 'center',
            lineHeight: mobile ? '1.4' : '1.6'
          }}>
            Score based on 20 opportunity trials (first {roundSummary.currentLevel} trials were setup)
            <br />
            Each dimension had exactly 6 matches to detect
            <br />
            <span style={{ color: '#10b981' }}>90%+ (18/20) on all = Level Up</span> • <span style={{ color: '#f59e0b' }}>&lt;75% (15/20) on any = Level Down</span>
            {roundSummary.interferenceRate > 0 && (
              <>
                <br />
                <span style={{ color: '#ec4899' }}>Interference: {Math.round(roundSummary.interferenceRate * 100)}% (trick trials)</span>
              </>
            )}
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: mobile ? '0.75rem' : '1rem'
          }}>
            {Object.entries(roundSummary.dimensionResults).map(([dimension, result]) => (
              <div key={dimension} style={{
                background: 'rgba(30, 41, 59, 0.6)',
                borderRadius: '8px',
                padding: '1rem',
                border: `2px solid ${DIMENSIONS[dimension].color}`
              }}>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#94a3b8',
                  marginBottom: '0.5rem'
                }}>
                  {DIMENSIONS[dimension].name}
                </div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: result.percentage >= 90 ? '#10b981' : result.percentage >= 70 ? '#60a5fa' : '#f59e0b',
                  marginBottom: '0.25rem'
                }}>
                  {result.percentage}%
                </div>
                <div style={{
                  fontSize: '0.75rem',
                  color: '#64748b'
                }}>
                  {result.correct} / {result.total} correct
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
          gap: mobile ? '0.75rem' : '1rem'
        }}>
          {/* Show "Sign Up" button if anonymous user hit their limit, otherwise show Continue Training */}
          {roundSummary.anonymous && anonRoundsToday >= anonDailyLimit ? (
            <button
              onClick={() => window.location.href = '/signup'}
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '1.25rem',
                fontSize: '1.1rem',
                fontWeight: '700',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Sign Up - Get 8 Rounds/Day
            </button>
          ) : (
            <button
              onClick={continueTraining}
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '1.25rem',
                fontSize: '1.1rem',
                fontWeight: '700',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <Play size={20} />
              Continue Training ({roundSummary.newLevel}-back)
            </button>
          )}
          <button
            onClick={resetGame}
            style={{
              background: 'rgba(100, 116, 139, 0.2)',
              border: '2px solid rgba(100, 116, 139, 0.5)',
              borderRadius: '12px',
              padding: '1.25rem',
              fontSize: '1.1rem',
              fontWeight: '700',
              color: '#cbd5e1',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <Settings size={20} />
            Take a Break
          </button>
        </div>
      </div>
      
      {/* Modals */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </div>
  );
};

export default RoundSummary;

